import { Fragment } from "react";
import { useTranslation } from "react-i18next";
import DOMPurify from "dompurify";
import { Body, Link, SectionHeader, SecondaryBody } from "@sprout/typography";
import { Field } from "./field";
import { DemographicSection } from "./demographic_section";
import { EeocForm } from "./eeoc/form";
import { ComplianceSection } from "./compliance_section";
import { EmploymentSection } from "./employment_section";
import { EducationSection } from "./education_section";
import EmailField from "./email_field";
import PhoneField from "./phone_field";
import { LocationInput } from "@components/ui/location_input";
import { employmentRequired, showEmployment } from "@utils/jben/job_helpers";
import { hasEducationData, hasEmploymentData } from "@utils/jben/build_quick_apply_profile";
import { isBasicField } from "@utils/jben/build_application.ts";
import { BoardConfigurationContext } from "@contexts/board_configuration_context";
import type { BoardConfiguration } from "@/types/jben/board_configuration.ts";
import type { JobPost, SearchSpecificFields } from "@/types/jben/job_post.ts";
import type { ApplicationFormState } from "./use_application_form";
import "./application_form.scss";

function sectionDataChanged<T>(current: T[], next: T[], hasData: (row: T) => boolean): boolean {
  return JSON.stringify(next.filter(hasData)) !== JSON.stringify(current.filter(hasData));
}

export const ApplyHeading = () => {
  const { t } = useTranslation("job_post");

  return (
    <div className="application--header">
      <div className="application--header--text">
        <div className="application--header--title">
          <SectionHeader as="h2">{t("application.apply_to_job")}</SectionHeader>
        </div>
        <div className="application--header--required">
          <div className="application--header--required--asterisk">
            <SecondaryBody>{"*"}</SecondaryBody>
          </div>
          <SecondaryBody>{t("application.required")}</SecondaryBody>
        </div>
      </div>
    </div>
  );
};

interface QuestionFieldsProps {
  form: ApplicationFormState;
  jobPost: JobPost & SearchSpecificFields;
  boardConfiguration: BoardConfiguration;
  educationApiUrl?: string;
}

export const QuestionFields = ({
  form,
  jobPost,
  boardConfiguration,
  educationApiUrl,
}: QuestionFieldsProps) => {
  const { t } = useTranslation("job_post");
  const {
    showBasicFields,
    fields,
    hasCustomFields,
    answers,
    errors,
    handleChange,
    handleFileInputChange,
    setPhoneError,
    candidateLocation,
    handleCandidateLocationChange,
    uploadManager,
    prefillComplete,
    currentUserEmail,
    employmentAnswers,
    setEmploymentAnswers,
    employmentErrors,
    educationAnswers,
    setEducationAnswers,
    educationErrors,
    displayEducationForm,
    demographicErrors,
    handleDemographicChange,
    eeocErrors,
    complianceAnswers,
    complianceErrors,
    handleComplianceChange,
    demographicDataConsentRequired,
    setQuickApplyProfileModified,
  } = form;

  const renderLocationField = (required: boolean) => (
    <div className="field-wrapper" data-provides="candidate-location">
      <LocationInput
        value={candidateLocation}
        onChange={handleCandidateLocationChange}
        required={required}
      />
      {errors.candidate_location && (
        <p
          id="candidate_location-error"
          className="helper-text helper-text--error"
          aria-live="polite"
        >
          {errors.candidate_location}
        </p>
      )}
    </div>
  );

  const renderFields = (renderCustom = false) => {
    return (
      <div className="application--questions">
        {renderCustom && showBasicFields && <hr />}
        {fields
          .filter((f) => f.custom === renderCustom)
          .filter((f) => showBasicFields || !isBasicField(f.name))
          .map((field) => {
            if (field.name === "phone") {
              return (
                <Fragment key={field.name}>
                  <div>
                    <PhoneField
                      key={prefillComplete ? "prefilled" : "initial"}
                      label={field.label}
                      name={field.name}
                      required={field.required}
                      countryError={errors.country}
                      phoneError={errors.phone}
                      value={answers[field.name] as string | null}
                      onChange={(value) => handleChange(field.name, value)}
                      onError={setPhoneError}
                    />
                  </div>
                </Fragment>
              );
            }

            if (field.name === "candidate_location") {
              return <Fragment key={field.name}>{renderLocationField(field.required)}</Fragment>;
            }

            if (field.name === "email") {
              return (
                <div key={field.name} className="field-wrapper">
                  <EmailField
                    label={field.label}
                    name={field.name}
                    type={field.type}
                    options={field.values}
                    required={field.required}
                    error={field.error}
                    disabled
                    onChange={() => {}}
                    onFocus={() => {}}
                    value={currentUserEmail ?? ""}
                  />
                </div>
              );
            }

            return (
              <div
                key={field.type === "input_file" ? `${field.name}-${prefillComplete}` : field.name}
                className={
                  field.type === "textarea"
                    ? "field-wrapper field-wrapper--multiline"
                    : "field-wrapper"
                }
              >
                <Field
                  label={field.label}
                  name={field.name}
                  type={field.type}
                  options={field.values}
                  required={field.required}
                  multiSelectStyle={field.multiSelectStyle}
                  error={field.error}
                  allowManual={field.allowManual}
                  onChange={(value) => {
                    const key = field.name;
                    if (field.type === "input_file") {
                      handleFileInputChange(key, value);
                    } else {
                      handleChange(key, value);
                    }
                  }}
                  onFocus={() => {}}
                  uploadManager={uploadManager}
                  value={answers[field.name]}
                  allowedFiletypes={field.allowedFiletypes}
                />

                {field.description && (
                  <Body as="div" secondary>
                    <div
                      id={`${field.name}-description`}
                      className="question-description"
                      dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(field.description) }}
                    ></div>
                  </Body>
                )}
              </div>
            );
          })}
      </div>
    );
  };

  return (
    <BoardConfigurationContext.Provider value={boardConfiguration}>
      {showBasicFields && (
        <>
          {renderFields(false)}
          {showEmployment(jobPost) && (
            <EmploymentSection
              employments={employmentAnswers}
              required={employmentRequired(jobPost)}
              errors={employmentErrors}
              onChange={(employments) => {
                if (sectionDataChanged(employmentAnswers, employments, hasEmploymentData)) {
                  setQuickApplyProfileModified(true);
                }
                setEmploymentAnswers(employments);
              }}
            />
          )}
          {displayEducationForm && educationApiUrl && (
            <EducationSection
              educationConfiguration={jobPost.education_config!}
              educationApiUrl={educationApiUrl}
              onChange={(educations) => {
                if (sectionDataChanged(educationAnswers, educations, hasEducationData)) {
                  setQuickApplyProfileModified(true);
                }
                setEducationAnswers(educations);
              }}
              answers={educationAnswers}
              errors={educationErrors}
            />
          )}
        </>
      )}

      {hasCustomFields && renderFields(true)}

      {jobPost.demographic_questions && (
        <>
          <hr />
          <DemographicSection
            title={jobPost.demographic_questions.title}
            description={jobPost.demographic_questions.description}
            questions={jobPost.demographic_questions.questions}
            errors={demographicErrors}
            onChange={handleDemographicChange}
          />
        </>
      )}

      {jobPost.eeoc_sections && (
        <EeocForm
          sections={jobPost.eeoc_sections}
          errors={eeocErrors}
          onChange={handleChange}
          answers={answers}
        />
      )}

      {jobPost.data_compliance && (
        <>
          <hr />
          <ComplianceSection
            internal={false}
            complianceAnswers={complianceAnswers}
            data={jobPost.data_compliance}
            errors={complianceErrors}
            companyName={jobPost.company_name}
            onChange={handleComplianceChange}
            demographicDataConsentRequired={demographicDataConsentRequired}
          />
        </>
      )}

      {jobPost.application_deadline && (
        <p>
          {t("post.application_deadline", {
            date: new Date(jobPost.application_deadline).toLocaleDateString(undefined, {
              year: "numeric",
              month: "long",
              day: "numeric",
            }),
          })}
        </p>
      )}

      {jobPost.include_ai_disclaimer && (
        <>
          <hr />
          <div className="application--ai-disclaimer">
            {jobPost.ai_disclaimer && (
              <div className="content">
                <div
                  dangerouslySetInnerHTML={{
                    __html: DOMPurify.sanitize(jobPost.ai_disclaimer),
                  }}
                />
                {jobPost.ai_opt_out_request_url && (
                  <Link href={jobPost.ai_opt_out_request_url} external>
                    {t("ai_disclaimer.learn_more")}
                  </Link>
                )}
              </div>
            )}
          </div>
        </>
      )}
    </BoardConfigurationContext.Provider>
  );
};
